import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { api, ApiError } from "./api.ts";
import { NotesToolDetails } from "./NotesToolDetails.tsx";
import {
  REPRESENTATIVE_ACTION_SCHEMAS,
  type RepresentativeActionResult,
  type RepresentativeNote,
} from "../../shared/representative.ts";
import { Button } from "@/components/ui/button.tsx";
import { Input } from "@/components/ui/input.tsx";
import { Textarea } from "@/components/ui/textarea.tsx";
import { Label } from "@/components/ui/label.tsx";
import { Alert, AlertDescription } from "@/components/ui/alert.tsx";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/card.tsx";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog.tsx";

type Draft = { id?: string; revision?: number; title: string; body: string };
export function RepresentativeNotes({
  representativeId,
  active,
  refreshVersion,
  onEditingChange,
  detailsEnabled = false,
}: {
  representativeId: string;
  active: boolean;
  refreshVersion: number;
  onEditingChange: (id: string | null) => void;
  detailsEnabled?: boolean;
}) {
  const [notes, setNotes] = useState<RepresentativeNote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [deleting, setDeleting] = useState<RepresentativeNote | null>(null);
  const [notice, setNotice] = useState("");
  const pending = useRef<{
    name: string;
    args: unknown;
    callId: string;
  } | null>(null);
  const submitting = useRef(false);
  const epoch = useRef(0);
  const load = useCallback(async () => {
    const attempt = ++epoch.current;
    setLoading(true);
    try {
      const data = await api<{ notes: RepresentativeNote[] }>(
        "/api/representative/notes",
        { avatarId: representativeId },
      );
      if (attempt === epoch.current) setNotes(data.notes);
    } catch {
      if (attempt === epoch.current)
        setError("Could not load your notes. Try again.");
    } finally {
      if (attempt === epoch.current) setLoading(false);
    }
  }, [representativeId]);
  useEffect(() => {
    if (active) void load();
    return () => {
      epoch.current++;
    };
  }, [active, refreshVersion, load]);
  useEffect(() => {
    onEditingChange(draft ? (draft.id ?? "new") : null);
  }, [draft, onEditingChange]);
  useEffect(() => () => onEditingChange(null), [onEditingChange]);

  const run = async (name: string, args: unknown) => {
    if (submitting.current) return;
    submitting.current = true;
    setBusy(true);
    setError(null);
    setNotice("");
    const command = pending.current ?? {
      name,
      args,
      callId: `manual_${crypto.randomUUID()}`,
    };
    pending.current = command;
    try {
      const { result } = await api<{ result: RepresentativeActionResult }>(
        "/api/representative/actions",
        { avatarId: representativeId, body: command },
      );
      pending.current = null;
      setUncertain(false);
      if (!result.success) {
        setError(
          result.error === "REVISION_CONFLICT"
            ? "This note changed while you were editing. Your draft is kept. Compare it with the saved note below, then cancel and reopen it to use the latest version."
            : result.instructionToRealtime,
        );
        await load();
        return;
      }
      setDraft(null);
      setDeleting(null);
      setNotice(
        name === "representative_delete_note" ? "Note deleted." : "Note saved.",
      );
      await load();
    } catch (error) {
      if (
        error instanceof ApiError &&
        error.status >= 400 &&
        error.status < 500
      ) {
        pending.current = null;
        setUncertain(false);
        setDeleting(null);
        setError(
          error.status === 409
            ? "This note changed while you were editing. Your draft is kept. Compare it with the saved note below, then cancel and reopen it to use the latest version."
            : error.status === 401
              ? "Your sign-in expired. Sign in again to continue."
              : "The note could not be saved. Check its details and refresh the saved notes.",
        );
        await load();
        return;
      }
      setUncertain(true);
      setError(
        "The response was lost. Check the result before making more changes; this uses the same request and cannot save a duplicate.",
      );
    } finally {
      submitting.current = false;
      setBusy(false);
    }
  };

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    const { title, body } = draft;
    const parsed =
      REPRESENTATIVE_ACTION_SCHEMAS.representative_create_note.safeParse({
        title,
        body,
      });
    if (!parsed.success) {
      setError(
        "Give your note a title of up to 200 characters and text of up to 20,000 characters.",
      );
      return;
    }
    void run(
      draft.id ? "representative_update_note" : "representative_create_note",
      draft.id
        ? { ...parsed.data, id: draft.id, revision: draft.revision }
        : parsed.data,
    );
  };
  const edit = (next: Draft) => {
    setError(null);
    setNotice("");
    setDraft(next);
  };

  return (
    <div className="space-y-6">
      {detailsEnabled && <NotesToolDetails key={representativeId} representativeId={representativeId} active={active} />}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Private to your account. Ask Destiny to remember a note, or write one
          yourself.
        </p>
        <Button
          variant="outline"
          disabled={!!draft || busy || uncertain}
          onClick={() => edit({ title: "", body: "" })}
        >
          <Plus /> New note
        </Button>
      </div>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {notice && (
        <p role="status" className="text-sm text-success-foreground">
          {notice}
        </p>
      )}
      {uncertain && (
        <Button
          disabled={busy}
          onClick={() => {
            if (pending.current)
              void run(pending.current.name, pending.current.args);
          }}
        >
          {busy ? "Checking..." : "Check result"}
        </Button>
      )}
      {draft && (
        <form
          onSubmit={save}
          className="space-y-4 rounded-xl border border-border p-4"
          aria-label="Edit note"
        >
          <p className="text-sm font-medium">
            You're editing. Destiny will wait before changing your notes.
          </p>
          <div className="space-y-2">
            <Label htmlFor="note-title">Title</Label>
            <Input
              id="note-title"
              required
              maxLength={200}
              disabled={busy || uncertain}
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="note-body">Note</Label>
            <Textarea
              id="note-body"
              rows={5}
              maxLength={20000}
              disabled={busy || uncertain}
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || uncertain}>
              {busy ? "Saving..." : "Save note"}
            </Button>
            <Button
              variant="ghost"
              type="button"
              disabled={busy || uncertain}
              onClick={() => {
                setDraft(null);
                setError(null);
              }}
            >
              Cancel editing
            </Button>
          </div>
        </form>
      )}
      {loading && (
        <p role="status" className="text-sm text-muted-foreground">
          Loading saved notes...
        </p>
      )}
      {!loading && !notes.length && (
        <p className="text-sm text-muted-foreground">
          No notes yet. Tell Destiny what you'd like to keep.
        </p>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {notes.map((note) => (
          <Card key={note.id}>
            <CardHeader>
              <CardTitle className="break-words">{note.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="whitespace-pre-wrap break-words text-sm">
                {note.body}
              </p>
              <p className="text-xs text-muted-foreground">
                Saved | version {note.revision}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!!draft || busy || uncertain}
                  onClick={() => edit({ ...note })}
                >
                  <Pencil /> Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={!!draft || busy || uncertain}
                  onClick={() => setDeleting(note)}
                >
                  <Trash2 /> Delete
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Button variant="ghost" onClick={() => void load()} disabled={loading}>
        Refresh notes
      </Button>
      <AlertDialog
        open={!!deleting}
        onOpenChange={(open) => {
          if (!open && !busy && !uncertain) setDeleting(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this note?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleting?.title}" will be permanently removed from your private
              notes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {uncertain && (
            <Alert variant="destructive">
              <AlertDescription>
                The result is unknown. Check it before continuing.
              </AlertDescription>
            </Alert>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy || uncertain}>
              Keep note
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={busy}
              onClick={(e) => {
                e.preventDefault();
                if (deleting)
                  void run("representative_delete_note", {
                    id: deleting.id,
                    revision: deleting.revision,
                  });
              }}
            >
              {busy ? "Deleting..." : uncertain ? "Check result" : "Delete note"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
